<template>
  <v-card flat class="workflow-states-progress">
    <v-card-title class="pb-0 pt-1 px-2 d-flex align-center" style="min-height: 32px;">
      <v-icon left color="primary" class="mr-1" x-small>mdi-sitemap</v-icon>
      <span class="text-caption font-weight-medium">Workflow States</span>
      <v-spacer></v-spacer>
      <v-btn 
        x-small 
        text 
        color="grey" 
        @click="clearWorkflowFilter"
        class="ml-1"
        style="height: 24px; min-height: 24px;"
      >
        <v-icon x-small class="mr-1">mdi-filter-remove</v-icon>
        <span class="hidden-sm-and-down">Clear Filter</span>
        <span class="hidden-md-and-up">Clear</span>
      </v-btn>
    </v-card-title>
    <v-card-text class="py-1 px-2">
      <div class="workflow-steps-container">
        <div class="d-flex align-center justify-space-between workflow-responsive-container">
          <!-- Main workflow states -->
          <div class="d-flex align-center flex-grow-1 workflow-main-steps">
            <template v-for="(state, index) in workflowStates">
              <div 
                :key="'step-' + state.id"
                class="workflow-step"
                :class="{ 
                  'clickable': true,
                  'selected': isStateSelected(state.id)
                }"
                @click="onStateClick(state.id)"
              >
                <!-- Step circle -->
                <div class="step-circle" :class="getStepClass(state.id)">
                  <span class="step-count-number">{{ getStateCount(state.id) }}</span>
                </div>
                
                <!-- Step content -->
                <div class="step-content">
                  <div class="step-title">{{ state.name }}</div>
                </div>
              </div>
              
              <!-- Arrow between steps (not after last step) -->
              <div v-if="index < workflowStates.length - 1" :key="'arrow-' + state.id" class="workflow-arrow">
                <v-icon x-small color="grey">mdi-chevron-right</v-icon>
              </div>
            </template>
            
          </div>
          
          <!-- Not Set section (separated) -->
          <div class="workflow-step not-set-step" :class="{ 
            'clickable': true,
            'selected': isStateSelected('not-set'),
            'ml-2': $vuetify.breakpoint.smAndDown,
            'ml-4': $vuetify.breakpoint.mdAndUp
          }" @click="onNotSetClick()">
            <div class="step-circle not-set" :class="{ 
              'has-items': notSetCount > 0,
              'selected': isStateSelected('not-set')
            }">
              <span class="step-count-number">{{ notSetCount }}</span>
            </div>
            <div class="step-content">
              <div class="step-title">Not Set</div>
            </div>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'WorkflowStatesProgress',
  props: {
    workflowStates: {
      type: Array,
      default: () => []
    },
    vaults: {
      type: Array,
      default: () => []
    },
    selectedStates: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    // Count of portals with no workflow state or orphaned workflow state
    notSetCount() {
      if (!this.vaults || this.vaults.length === 0) return 0;
      
      return this.vaults.filter(vault => {
        // No workflow state ID set
        if (!vault.workflowStateId) return true;
        
        // Workflow state ID is greater than available states (orphaned)
        const maxStateId = this.workflowStates.length > 0 ? Math.max(...this.workflowStates.map(s => s.id)) : 0;
        if (vault.workflowStateId > maxStateId) return true;
        
        // Workflow state ID doesn't exist in current states (orphaned)
        const stateExists = this.workflowStates.find(state => state.id === vault.workflowStateId);
        return !stateExists;
      }).length;
    }
  },
  methods: {
    // Get count of portals for a specific workflow state
    getStateCount(stateId) {
      if (!this.vaults || this.vaults.length === 0) return 0;
      
      return this.vaults.filter(vault => vault.workflowStateId === stateId).length;
    },
    
    // Get CSS class for step circle based on count and selection
    getStepClass(stateId) {
      const count = this.getStateCount(stateId);
      const isSelected = this.isStateSelected(stateId);
      return {
        'has-items': count > 0,
        'empty': count === 0,
        'selected': isSelected
      };
    },
    
    // Check if a state is currently selected
    isStateSelected(stateId) {
      return this.selectedStates.includes(stateId);
    },
    
    // Handle click on workflow state
    onStateClick(stateId) {
      // Always emit click for selection/deselection, regardless of count
      this.$emit('state-clicked', stateId);
    },
    
    // Handle click on "Not Set"
    onNotSetClick() {
      // Always emit click for selection/deselection, regardless of count
      this.$emit('state-clicked', 'not-set');
    },
    
    // Clear workflow filter
    clearWorkflowFilter() {
      this.$emit('clear-filter');
    }
  }
}
</script>

<style scoped>
.workflow-states-progress {
  background: rgb(255, 252, 247);
  border: 1px solid #e8dcc6;
  border-radius: 6px;
  width: fit-content;
  max-width: 100%;
}

.workflow-steps-container {
  min-height: 32px;
}

.workflow-responsive-container {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
}

.workflow-main-steps {
  position: relative;
  flex-shrink: 1;
  min-width: 0;
}

.workflow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
  min-width: 60px;
  max-width: 85px;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .workflow-step {
    min-width: 50px;
    max-width: 70px;
  }
  
  .workflow-states-progress {
    border-radius: 4px;
  }
}

.workflow-step.clickable {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.workflow-step.clickable:hover {
  transform: translateY(-2px);
}

.workflow-step.selected {
  transform: translateY(-2px);
  filter: brightness(1.1);
}

.workflow-step.selected .step-circle {
  box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.3);
  border-color: #2196f3;
}

.workflow-step.not-set-step {
  flex: 0 0 auto;
  border-left: 2px dashed #cccccc;
  padding-left: 8px;
  min-width: 60px;
  max-width: 80px;
}

/* Mobile not-set-step adjustments */
@media (max-width: 600px) {
  .workflow-step.not-set-step {
    padding-left: 6px;
    min-width: 50px;
    max-width: 65px;
  }
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e0e0e0;
  border: 2px solid #bdbdbd;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  z-index: 1;
  position: relative;
}

/* Mobile step circle adjustments */
@media (max-width: 600px) {
  .step-circle {
    width: 24px;
    height: 24px;
    margin-bottom: 1px;
  }
}

.step-circle.has-items {
  background: #4caf50;
  border-color: #388e3c;
  color: white;
}

.step-circle.empty {
  background: #f5f5f5;
  border-color: #e0e0e0;
  color: #9e9e9e;
}

.step-circle.not-set {
  background: #ff9800;
  border-color: #f57c00;
  color: white;
}

.step-circle.not-set.has-items {
  background: #ff5722;
  border-color: #d84315;
}

.step-circle.selected {
  background: #2196f3 !important;
  border-color: #1976d2 !important;
  color: white !important;
}

.step-circle.selected.not-set {
  background: #1976d2 !important;
  border-color: #0d47a1 !important;
}

.workflow-arrow {
  display: flex;
  align-items: center;
  margin: 0 4px;
}

/* Mobile workflow arrow adjustments */
@media (max-width: 600px) {
  .workflow-arrow {
    margin: 0 2px;
  }
}

.step-content {
  text-align: center;
  min-width: 0;
}

.step-title {
  font-weight: 600;
  font-size: 11px;
  color: #424242;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 1px;
  line-height: 1;
}

/* Mobile step title adjustments */
@media (max-width: 600px) {
  .step-title {
    font-size: 10px;
    margin-bottom: 0;
  }
}

.step-count {
  font-size: 10px;
  color: #757575;
  line-height: 1;
}

.step-count-number {
  font-weight: 600;
  font-size: 12px;
  line-height: 1;
}

/* Mobile step count number adjustments */
@media (max-width: 600px) {
  .step-count-number {
    font-size: 11px;
  }
}


.workflow-step.clickable .step-circle.has-items {
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.workflow-step.clickable:hover .step-circle.has-items {
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  transform: scale(1.05);
}

.not-set-step.clickable .step-circle.has-items {
  box-shadow: 0 2px 8px rgba(255, 87, 34, 0.3);
}

.not-set-step.clickable:hover .step-circle.has-items {
  box-shadow: 0 4px 12px rgba(255, 87, 34, 0.4);
  transform: scale(1.05);
}
</style>
